/** @format */

import { useState, useEffect } from "react";
import {
  collection,
  getDocs,
  query,
  limit,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  where,
  orderBy,
  serverTimestamp,
  getDoc,
} from "firebase/firestore";
import { db } from "../firebase/config";

export const useFirestore = (collectionName, options = {}) => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchDocuments = async () => {
      try {
        console.log(`🔍 Fetching ${collectionName}...`);
        setLoading(true);

        let docsQuery = collection(db, collectionName);

        // Apply filters if provided
        if (options.filterByCurrentUser && options.currentUser) {
          docsQuery = query(
            docsQuery,
            where("createdBy", "==", options.currentUser.uid)
          );
        }

        // Apply ordering
        if (options.orderByField) {
          docsQuery = query(
            docsQuery,
            orderBy(options.orderByField, options.orderDirection || "desc")
          );
        }

        // Apply limit
        docsQuery = query(docsQuery, limit(options.limit || 50));

        const docsSnapshot = await getDocs(docsQuery);

        console.log(`✅ Found ${docsSnapshot.size} ${collectionName}`);

        const docsData = docsSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        setDocuments(docsData);
        setError(null);
      } catch (err) {
        console.error(`❌ Error fetching ${collectionName}:`, err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (collectionName) {
      fetchDocuments();
    }
  }, [
    collectionName,
    options.currentUser?.uid,
    options.orderByField,
    options.orderDirection,
    options.limit,
  ]);

  // Add document function
  const addDocument = async (data) => {
    try {
      const docData = {
        ...data,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };

      const docRef = await addDoc(collection(db, collectionName), docData);
      console.log(`✅ Document added with ID: ${docRef.id}`);

      // Refresh the documents list
      const newDoc = { id: docRef.id, ...docData };
      setDocuments((prev) => [newDoc, ...prev]);

      return docRef;
    } catch (err) {
      console.error(`❌ Error adding document to ${collectionName}:`, err);
      throw err;
    }
  };

  // Update document function
  const updateDocument = async (id, data) => {
    try {
      const docRef = doc(db, collectionName, id);
      const updateData = {
        ...data,
        updatedAt: serverTimestamp(),
      };

      await updateDoc(docRef, updateData);
      console.log(`✅ Document updated: ${id}`);

      // Refresh the updated document from Firestore to reflect field deletions
      try {
        const refreshed = await getDoc(docRef);
        if (refreshed.exists()) {
          const refreshedData = { id, ...refreshed.data() };
          setDocuments((prev) =>
            prev.map((d) => (d.id === id ? refreshedData : d))
          );
        } else {
          // If no longer exists, remove from local list
          setDocuments((prev) => prev.filter((d) => d.id !== id));
        }
      } catch (_) {
        // Fallback to local merge if refresh fails
        setDocuments((prev) =>
          prev.map((d) => (d.id === id ? { ...d, ...updateData } : d))
        );
      }

      return true;
    } catch (err) {
      console.error(`❌ Error updating document in ${collectionName}:`, err);
      throw err;
    }
  };

  // Delete document function
  const deleteDocument = async (id) => {
    try {
      const docRef = doc(db, collectionName, id);
      await deleteDoc(docRef);
      console.log(`✅ Document deleted: ${id}`);

      // Remove from the documents list
      setDocuments((prev) => prev.filter((doc) => doc.id !== id));

      return true;
    } catch (err) {
      console.error(`❌ Error deleting document from ${collectionName}:`, err);
      throw err;
    }
  };

  return {
    documents,
    loading,
    error,
    addDocument,
    updateDocument,
    deleteDocument,
    refetch: () => window.location.reload(), // Simple refresh
  };
};

// Hook for subcollections - simplified version
export const useSubcollection = (
  parentCollection,
  parentId,
  subcollectionName
) => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchSubcollectionDocuments = async () => {
      if (!parentId) return;

      try {
        setLoading(true);
        const subcollectionRef = collection(
          db,
          parentCollection,
          parentId,
          subcollectionName
        );
        const querySnapshot = await getDocs(subcollectionRef);
        const docs = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setDocuments(docs);
        setError(null);
      } catch (err) {
        console.error(`Error fetching ${subcollectionName}:`, err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchSubcollectionDocuments();
  }, [parentCollection, parentId, subcollectionName]);

  return {
    documents,
    loading,
    error,
    refetch: () => window.location.reload(),
  };
};
